import styles from './DeliveryOverview.module.css';
import Icon from '../../components/common/Icon';

export interface DeliveryState {
  intelligentTiering: boolean;
  intelligentTieringSince: string | null;
  downloadsViaCloudfront: boolean;
  cloudfrontConfigured: boolean;
  maxCdnDownloadGb: number;
}

export interface ArchiveState {
  enabled: boolean;
  delayedDownloadDays: number;
  fullArchiveDays: number;
}

interface Rates {
  s3UsdPerGbMonth: number;
  s3InfrequentUsdPerGbMonth: number;
  s3InstantArchiveUsdPerGbMonth: number;
  s3ArchiveUsdPerGbMonth: number;
  egressUsdPerGb: number;
  intermediateKeepDays: number;
}

interface Props {
  delivery: DeliveryState;
  archive: ArchiveState;
  rates: Rates;
  cdnPlanLabel: string;
  cdnRecommendedLabel: string;
}

interface Step { title: string; detail?: string; tone?: 'cdn' | 's3' | 'cold' | 'off' }

const price = (n: number) => `$${Number((n ?? 0).toPrecision(3))}`;
const day = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const Flow = ({ label, steps, cost }: { label: string; steps: Step[]; cost: string }) => (
  <div className={styles.flow}>
    <ol className={styles.steps} aria-label={label}>
      {steps.map((s) => (
        <li key={s.title} className={`${styles.step} ${s.tone ? styles[s.tone] : ''}`}>
          <span className={styles.stepTitle}>{s.title}</span>
          {s.detail && <span className={styles.stepDetail}>{s.detail}</span>}
        </li>
      ))}
    </ol>
    <span className={styles.cost}>{cost}</span>
  </div>
);

/**
 * How the platform stores and serves files, drawn from its current settings:
 * where bytes sit in S3 and how they get cheaper, which path each kind of
 * viewer traffic takes, and where downloads come from.
 */
const DeliveryOverview = ({ delivery, archive, rates, cdnPlanLabel, cdnRecommendedLabel }: Props) => {
  const tiering = delivery.intelligentTiering;
  const cdnDownloads = delivery.downloadsViaCloudfront && delivery.cloudfrontConfigured;
  const rawCold = archive.enabled;
  const derivedCold = archive.enabled && archive.fullArchiveDays > 0;
  const egress = `S3 egress ${price(rates.egressUsdPerGb)}/GB`;

  const inS3: Step = tiering
    ? {
      title: 'S3 Intelligent-Tiering',
      detail: `Frequent ${price(rates.s3UsdPerGbMonth)} → 30 days unread: Infrequent ${price(rates.s3InfrequentUsdPerGbMonth)} → 90 days: Archive Instant ${price(rates.s3InstantArchiveUsdPerGbMonth)}. Any open or download puts a file back in Frequent.`,
      tone: 's3',
    }
    : { title: 'S3 Standard', detail: `${price(rates.s3UsdPerGbMonth)} per GB-month, whether anyone opens the file or not`, tone: 's3' };

  return (
    <details className={styles.panel} open>
      <summary className={styles.summary}>
        <span className={styles.title}>How files are stored and served</span>
        <span className={styles.chips}>
          <span className={`${styles.chip} ${tiering ? styles.on : styles.offChip}`}>
            {tiering
              ? `Intelligent-Tiering${delivery.intelligentTieringSince ? ` since ${day(delivery.intelligentTieringSince)}` : ''}`
              : 'S3 Standard'}
          </span>
          <span className={`${styles.chip} ${cdnDownloads ? styles.on : styles.offChip}`}>
            Downloads: {cdnDownloads ? `CloudFront ≤ ${delivery.maxCdnDownloadGb} GB` : 'S3'}
          </span>
          <span className={styles.chip}>
            CloudFront {cdnPlanLabel}
            {cdnRecommendedLabel && cdnRecommendedLabel !== cdnPlanLabel && ` · fits ${cdnRecommendedLabel}`}
          </span>
        </span>
      </summary>

      <p className={styles.note}>
        Where every byte lives and which way it reaches a browser, from the current settings
        (Platform Settings → Storage &amp; Delivery and File Archiving; the CloudFront plan is in Unit
        prices below). The browser fetches files from CloudFront or S3 directly; the app server only
        signs the links (on QA it also streams octrees, drawings and models).
      </p>

      <section className={styles.lane} aria-labelledby="delivery-storage">
        <h4 id="delivery-storage" className={styles.laneTitle}>
          <Icon name="database" aria-hidden />
          In the bucket
        </h4>
        <Flow
          label="Originals"
          steps={[
            { title: 'Upload', detail: 'browser → S3 in 50 MB parts; the file name is stored on the object' },
            { ...inS3, title: `Original — ${inS3.title}` },
            rawCold
              ? { title: `Glacier after ${archive.delayedDownloadDays} days`, detail: `${price(rates.s3ArchiveUsdPerGbMonth)}/GB-month; still viewable, downloading becomes a 3–5 h request`, tone: 'cold' }
              : { title: 'Never archived', detail: 'File Archiving is off', tone: 'off' },
          ]}
          cost="storage"
        />
        <Flow
          label="Converted files"
          steps={[
            { title: 'Conversion', detail: 'octrees, panoramas, splats, videos, drawings, models, orthomosaics' },
            { ...inS3, title: `Viewer files — ${inS3.title}` },
            derivedCold
              ? { title: `Glacier after ${archive.fullArchiveDays} days`, detail: 'not viewable until the project is unarchived', tone: 'cold' }
              : { title: 'Stay in S3', detail: 'always viewable; no full archive', tone: 'off' },
          ]}
          cost="storage"
        />
        <p className={styles.footnote}>
          Thumbnails (under 128 KB) stay in S3 Standard. The laz/ and harmonize/ working files are
          deleted after {rates.intermediateKeepDays} days.
        </p>
      </section>

      <section className={styles.lane} aria-labelledby="delivery-viewing">
        <h4 id="delivery-viewing" className={styles.laneTitle}>
          <Icon name="visibility" aria-hidden />
          Viewing
        </h4>
        {delivery.cloudfrontConfigured ? (
          <Flow
            label="Every viewer"
            steps={[
              { title: 'Point clouds · splats · 360 videos · panoramas · orthomosaics · drawings · 3D models' },
              { title: 'CloudFront', detail: 'signed cookies / URLs; each piece cached at the edge for 24 h, cleared when a file is converted again', tone: 'cdn' },
              { title: 'Browser' },
            ]}
            cost={`${cdnPlanLabel} plan allowance`}
          />
        ) : (
          <>
            <Flow
              label="Direct from S3"
              steps={[
                { title: 'Splats · 360 videos · panoramas · orthomosaics' },
                { title: 'Presigned S3 link', detail: 'valid 1 hour; this server has no CloudFront distribution', tone: 's3' },
                { title: 'Browser' },
              ]}
              cost={egress}
            />
            <Flow
              label="Through the app server"
              steps={[
                { title: 'Point clouds · drawings · 3D models' },
                { title: 'App server', detail: 'streamed from S3, never stored', tone: 's3' },
                { title: 'Browser' },
              ]}
              cost={egress}
            />
          </>
        )}
        <Flow
          label="PDFs"
          steps={[
            { title: 'PDFs · documents' },
            { title: 'Presigned S3 link', detail: "the browser's own reader loads the whole file", tone: 's3' },
            { title: 'Browser' },
          ]}
          cost={egress}
        />
        {tiering && (
          <p className={styles.footnote}>Every open is a read: it keeps that file&apos;s viewer files in Frequent Access for another 30 days.</p>
        )}
      </section>

      <section className={styles.lane} aria-labelledby="delivery-downloads">
        <h4 id="delivery-downloads" className={styles.laneTitle}>
          <Icon name="download" aria-hidden />
          Downloads of originals
        </h4>
        {cdnDownloads ? (
          <>
            <Flow
              label="Through CloudFront"
              steps={[
                { title: `Up to ${delivery.maxCdnDownloadGb} GB`, detail: 'uploaded since names are stored on the object' },
                { title: 'CloudFront signed link', detail: '1 hour; 5 minutes from a share link', tone: 'cdn' },
                { title: 'Browser' },
              ]}
              cost={`${cdnPlanLabel} plan allowance`}
            />
            <Flow
              label="From S3"
              steps={[
                { title: `Over ${delivery.maxCdnDownloadGb} GB, archived or older uploads` },
                { title: 'Presigned S3 link', detail: 'names the file in the link', tone: 's3' },
                { title: 'Browser' },
              ]}
              cost={egress}
            />
          </>
        ) : (
          <Flow
            label="From S3"
            steps={[
              { title: 'Every download' },
              { title: 'Presigned S3 link', detail: 'names the file in the link', tone: 's3' },
              { title: 'Browser' },
            ]}
            cost={egress}
          />
        )}
        {delivery.downloadsViaCloudfront && !delivery.cloudfrontConfigured && (
          <p className={styles.footnote}>
            Downloads through CloudFront are switched on, but this server has no distribution
            configured, so its downloads stay on S3.
          </p>
        )}
      </section>
    </details>
  );
};

export default DeliveryOverview;
